import { fill, omitBy } from 'lodash';
import * as Yup from 'yup';

export const luggageTypeRegex = /^luggage-type-(\d+)/;

const getLuggageCount = (value) =>
  Object.keys(value || {}).reduce((accumulator, key) => {
    if (luggageTypeRegex.test(key)) {
      return accumulator + value[key];
    }
    return accumulator;
  }, 0);

export function hasLuggage(value) {
  return getLuggageCount(value) > 0;
}

export const luggageFormSchema = Yup.object().shape({
  confirmLuggageDetails: Yup.boolean().when('needConfirm', {
    is: true,
    then: (schema) => schema.oneOf([true]).required(),
  }),
});

export const getFormValuesToSubmit = (formValues) => {
  const keysToRemove = ['confirmLuggageDetails', 'needConfirm'];

  return omitBy({ ...formValues, submitted: true }, (v, k) =>
    keysToRemove.includes(k)
  );
};

export const transferLuggageFormToArray = (formValues) =>
  Object.keys(formValues).reduce((accumulator, key) => {
    const count = formValues[key];
    if (luggageTypeRegex.test(key) && count > 0) {
      const id = Number(key.match(luggageTypeRegex)[1]);
      const arr = new Array(count);
      fill(arr, { vehicle_type_id: id });
      return arr.concat(accumulator);
    }
    return accumulator;
  }, []);

export const getRelatedLuggageTypesFunc =
  ({ luggageGroups, luggageTypes }) =>
  (groupIds) => {
    const groups = groupIds.map((id) => luggageGroups[id]);
    return groups.reduce((qtIdsOfAll, currentGroup) => {
      const types = currentGroup.luggageTypes.map((id) => luggageTypes[id]);
      const typeQtIds = types.reduce(
        (qtIds, type) => qtIds.concat(type.qtLuggageTypeId),
        []
      );
      return qtIdsOfAll.concat(typeQtIds);
    }, []);
  };

/**
 * Get all QT luggage type IDs for categories with onlyForSameDayReturn: true
 * Used to identify which luggage items need to be removed when dates change from same-day to different-day return
 *
 * @param {Array} sameDayCategories - Categories with onlyForSameDayReturn: true
 * @param {Object} luggageGroups - Luggage groups keyed by ID
 * @param {Object} luggageTypes - Luggage types keyed by ID
 * @returns {Array<number>} Array of QT luggage type IDs
 */
export const getSameDayLuggageTypeIds = (
  sameDayCategories,
  luggageGroups,
  luggageTypes
) => {
  if (!sameDayCategories || !luggageGroups || !luggageTypes) {
    return [];
  }

  return sameDayCategories.reduce((allTypeIds, category) => {
    const groupIds = category.luggageGroups || [];
    const groups = groupIds.map((id) => luggageGroups[id]).filter(Boolean);

    const typeIds = groups.reduce((groupTypeIds, group) => {
      const types = (group.luggageTypes || [])
        .map((id) => luggageTypes[id])
        .filter(Boolean);
      const qtIds = types.map((type) => type.qtLuggageTypeId);
      return groupTypeIds.concat(qtIds);
    }, []);

    return allTypeIds.concat(typeIds);
  }, []);
};
